import { useCallback, useMemo, useState } from "react";
import useUserPageParams from "../../../../../hooks/useUserPageParams";
import { Separator } from "../../../../../components/ui/separator";
import { Button } from "../../../../../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { useWorkspaceObjectSchema, useWorkspaceObjectsDetailed } from "../../../../../hooks/QueryHooks";
import TanStackTable from "../../../../../shared/table/TanStackTable";
import { LegacyColumn } from "../../../../../shared/table/legacyTanStackAdapter";
import useObjectSearchTable from "../../../../../shared/table/useObjectSearchTable";
import EmptyTabState from "../../../../../shared/ui/EmptyTabState";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { parseObjectSearchRecord } from "../../../../../shared/emails-meetings/objectSearchAdapter";
import CustomObjectCardView from "../../account-custom-object/CustomObjectCardView";
import ObjectTabToolbar, { getSearchPlaceholder } from "../../account-custom-object/ObjectTabToolbar";
import NotesView from "../../account-custom-object/renderers/NotesView";
import DealsView from "../../account-custom-object/renderers/DealsView";
import TasksView from "../../account-custom-object/renderers/TasksView";
import RecordingsView from "../../account-custom-object/renderers/RecordingsView";

/** Slugs that have specialized renderers */
const SPECIALIZED_SLUGS = new Set(["notes", "deals", "tasks", "tickets", "meeting_recordings"]);

/** Default view mode per slug */
const getDefaultViewMode = (slug: string): "list" | "card" => {
  if (["notes", "meeting_recordings"].includes(slug)) return "card";
  return "list";
};

const getStoredViewMode = (slug: string): "list" | "card" | null => {
  try {
    const stored = localStorage.getItem(`custom-object-view-${slug}`);
    if (stored === "list" || stored === "card") return stored;
  } catch { /* ignore */ }
  return null;
};

const UserCustomObjectTab = () => {
  const { objectSlug, userId } = useUserPageParams() as { objectSlug: string; userId: string };
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const isSpecialized = SPECIALIZED_SLUGS.has(objectSlug || "");

  const [viewMode, setViewMode] = useState<"list" | "card">(
    getStoredViewMode(objectSlug || "") ?? getDefaultViewMode(objectSlug || ""),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const handleViewModeChange = (mode: "list" | "card") => {
    setViewMode(mode);
    try {
      localStorage.setItem(`custom-object-view-${objectSlug}`, mode);
    } catch { /* ignore */ }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  });

  const { data: workspaceObjects } = useWorkspaceObjectsDetailed({
    workspaceId: workspaceId || "",
    enabled: !!workspaceId,
  }) as any;

  const objectDef = useMemo(() => {
    if (!workspaceObjects || !objectSlug) return null;
    const objects = Array.isArray(workspaceObjects) ? workspaceObjects : workspaceObjects?.data || [];
    return objects.find((obj: any) => obj.api_slug === objectSlug);
  }, [workspaceObjects, objectSlug]);

  const traits = useMemo(
    () => workspaceSchema?.fieldsByObjectSlug?.[objectSlug!] || { list: [], obj: {} },
    [workspaceSchema?.fieldsByObjectSlug, objectSlug],
  );

  const filteredTraits = useMemo(
    () => traits?.list?.filter((item: any) => !item.exclude_display) || [],
    [traits?.list],
  );

  const selectedFields = useMemo(
    () => isSpecialized ? [] : filteredTraits.map((t: any) => t.name),
    [filteredTraits, isSpecialized],
  );

  const legacyColumns = useMemo<LegacyColumn[]>(() => {
    if (!isSpecialized && filteredTraits.length > 0) {
      return filteredTraits.map((field: any) => ({
        key: field.name,
        title: field.display_name,
        dataType: field.data_type,
        isSortable: true,
        attributeType: field.attribute_type,
      }));
    }
    return [];
  }, [filteredTraits, isSpecialized]);

  // Scope filter to this user (not account)
  const scopedFilter = useMemo(() => ({
    query_type: "AND",
    predicates: [{
      field: "users.external_id",
      operator: "eq",
      type: "string",
      value: userId,
      entity_type: "column",
    }],
  }), [userId]);

  const {
    rows,
    total,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setSort,
    setPageIndex,
    isLoading,
    isFetching,
  } = useObjectSearchTable({
    workspaceId: workspaceId || "",
    entityType: objectSlug || "",
    enabled: !!workspaceId && !!userId && !!objectSlug,
    initialPageSize: isSpecialized ? 25 : 15,
    initialSort: {
      field: `${objectSlug}.created_at`,
      order: "desc" as const,
    },
    queryOptions: {
      filter: scopedFilter,
      selectedFields: isSpecialized ? undefined : selectedFields,
      fallbackSortField: `${objectSlug}.created_at`,
    },
    parseRows: (rawRows: any[]) => {
      if (isSpecialized) {
        return (rawRows || []).map((record: any) =>
          parseObjectSearchRecord(record, objectSlug),
        );
      }
      return (rawRows || []).map((record: any) => {
        const row: any = {};
        const fields = record?.fields || {};
        Object.entries(fields).forEach(([key, value]) => {
          row[`${objectSlug}.${key}`] = value;
        });
        if (!row[`${objectSlug}.external_id`]) {
          row[`${objectSlug}.external_id`] = record?.id || fields?.external_id;
        }
        row._raw = { id: record?.id, fields, ...fields };
        return row;
      });
    },
    resetPageDeps: [userId, objectSlug, JSON.stringify(selectedFields || [])],
  });

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = (nextSorting || []).slice(0, 1)?.[0];
    if (!nextSort?.id) return;
    setSort({ field: nextSort.id, order: nextSort.desc ? "desc" : "asc" });
  }, [sorting, setSort]);

  const records = useMemo(() => {
    if (isSpecialized) {
      return rows.map((r: any) => r.meta_data || r);
    }
    return rows.map((row: any) => row._raw || {});
  }, [rows, isSpecialized]);

  const cardFieldDefs = useMemo(() => {
    if (objectDef?.fields) {
      return objectDef.fields.filter((f: any) => !f.exclude_display);
    }
    return filteredTraits.map((t: any) => ({
      column_name: t.name.includes(".") ? t.name.split(".").slice(1).join(".") : t.name,
      display_name: t.display_name,
      data_type: t.data_type,
      exclude_display: false,
      display_order: t.display_order,
    }));
  }, [objectDef?.fields, filteredTraits]);

  const objectName = objectDef?.display_name || objectSlug?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) || "Objects";

  const effectiveColumns = useMemo(() => {
    if (legacyColumns.length > 0) return legacyColumns;
    if (!isSpecialized && rows.length > 0) {
      return Object.keys(rows[0])
        .filter((k) => !k.startsWith("_") && k !== "id")
        .slice(0, 6)
        .map((key) => ({
          key,
          title: (key.includes(".") ? key.split(".").slice(1).join(".") : key)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          isSortable: true,
        }));
    }
    return [];
  }, [legacyColumns, rows, isSpecialized]);

  const noResults = !isLoading && !isFetching && rows.length === 0;

  if (noResults && !searchQuery) {
    return (
      <EmptyTabState
        title={`No ${objectName.toLowerCase()} yet`}
        description={`${objectName} associated with this contact will appear here.`}
      />
    );
  }

  const renderSpecializedView = () => {
    switch (objectSlug) {
      case "notes":
        return <NotesView records={records} searchQuery={searchQuery} />;
      case "deals":
        return (
          <DealsView
            records={records}
            searchQuery={searchQuery}
            stageFilter={filterValues.stage}
          />
        );
      case "tasks":
      case "tickets":
        return (
          <TasksView
            records={records}
            searchQuery={searchQuery}
            statusFilter={filterValues.status}
            priorityFilter={filterValues.priority}
            objectName={objectName}
          />
        );
      case "meeting_recordings":
        return <RecordingsView records={records} searchQuery={searchQuery} />;
      default:
        return null;
    }
  };

  const renderGenericView = () => {
    if (viewMode === "card") {
      return (
        <CustomObjectCardView
          records={records}
          fieldDefinitions={cardFieldDefs}
        />
      );
    }
    return (
      <TanStackTable
        data={rows}
        legacyColumns={effectiveColumns}
        totalData={total}
        itemType={objectName}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageCount={pageCount}
        onPageIndexChange={setPageIndex}
        sorting={sorting}
        onSortingChange={onTableSortingChange}
        loading={isLoading || isFetching}
        showPagination={Number(total) > 15}
        enableColumnReorder={false}
        enableColumnPinning={false}
      />
    );
  };

  const totalPages = pageCount;

  return (
    <div className="h-full bg-white flex flex-col overflow-hidden">
      <ObjectTabToolbar
        objectName={objectName}
        objectSlug={objectSlug || ""}
        workspaceId={workspaceId || ""}
        total={Number(total)}
        isLoading={isLoading || isFetching}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={getSearchPlaceholder(objectSlug || "")}
        viewMode={isSpecialized ? undefined : viewMode}
        onViewModeChange={isSpecialized ? undefined : handleViewModeChange}
        showViewToggle={!isSpecialized}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
      />

      <div className="flex-1 overflow-auto">
        {isSpecialized ? renderSpecializedView() : renderGenericView()}
      </div>

      {/* Pagination footer for specialized views */}
      {isSpecialized && totalPages > 1 && (
        <div className="p-3 border-t bg-muted/30 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            disabled={pageIndex === 0}
            onClick={() => setPageIndex(pageIndex - 1)}
          >
            <ChevronLeft size={14} />
            Previous
          </Button>
          <span className="text-xs text-muted-foreground px-2">
            Page {pageIndex + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            disabled={pageIndex >= totalPages - 1}
            onClick={() => setPageIndex(pageIndex + 1)}
          >
            Next
            <ChevronRight size={14} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default UserCustomObjectTab;
